/**
 * Sign-up prompt eligibility predicate (Phase 1 / W3.T6 / AUTH-08).
 *
 * Pure function so the trigger policy is testable in isolation without
 * mounting the full App tree.
 *
 * Triggers eligible only when:
 *   - user exists AND user.isAnonymous === true (signed-in users don't need
 *     the prompt)
 *   - the once-per-session anti-nag flag is NOT set in sessionStorage
 *     (key: `puzzly_signup_shown`)
 *
 * Used by App.jsx's two trigger entry points:
 *   - triggerSignUpForUpgrade (Phase-2 hook)
 *   - triggerSignUpForSaveAcrossDevices (publish handler)
 *
 * Never fires on first-puzzle-create — by design, the publish handler that
 * calls this is invoked AFTER `setCreatedPuzzles` adds the new puzzle, so
 * the prompt appears AT publish time (not before). The session flag prevents
 * re-showing on subsequent publishes.
 */

export const SIGNUP_SESSION_FLAG = "puzzly_signup_shown";

/**
 * Is the SignUpPrompt eligible to show right now?
 *
 * @param {object|null} user — the current Firebase user (or null)
 * @param {Storage|undefined} storage — sessionStorage-shaped object
 * @returns {boolean}
 */
export function isSignUpPromptEligible(user, storage) {
  if (!user || !user.isAnonymous) return false;
  if (!storage) return true;
  try {
    return storage.getItem(SIGNUP_SESSION_FLAG) !== "true";
  } catch (e) {
    return true;
  }
}

/**
 * Mark the session flag — call AFTER showing the prompt to prevent re-show.
 * Swallows errors (sessionStorage may be absent in some test envs).
 *
 * @param {Storage|undefined} storage
 */
export function markSignUpPromptShown(storage) {
  if (!storage) return;
  try {
    storage.setItem(SIGNUP_SESSION_FLAG, "true");
  } catch (e) {
    /* ignore */
  }
}

/**
 * Clear the once-per-session flag — call on sign-out so the fresh anonymous
 * session that re-bootstraps after logout becomes eligible for the
 * "save across devices" prompt again at its next publish.
 * Swallows errors (sessionStorage may be absent in some test envs).
 *
 * @param {Storage|undefined} storage
 */
export function clearSignUpPromptShown(storage) {
  if (!storage) return;
  try {
    storage.removeItem(SIGNUP_SESSION_FLAG);
  } catch (e) {
    /* ignore */
  }
}
